import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, CheckCircle, Loader, Edit3, AlertCircle, Clock, Zap } from 'lucide-react';
import { trackCTAClick } from '../lib/analytics';

/**
 * Estimate generation time based on file size
 */
const estimateGenerationTime = (fileSizeBytes) => {
  const sizeMB = fileSizeBytes / 1024 / 1024;
  // Base time + scaling factor based on file size
  // Small files (<5MB): ~30s, Medium (5-20MB): ~45-60s, Large (>20MB): ~90s+
  if (sizeMB < 5) return { min: 20, max: 40, label: '20-40 seconds' };
  if (sizeMB < 20) return { min: 40, max: 90, label: '40-90 seconds' };
  return { min: 90, max: 180, label: '1.5-3 minutes' };
};

/**
 * Single File Upload Component
 */
export const FileUploader = ({
  file,
  onFileSelect,
  onUpload,
  onEditPoints,
  uploading,
  processing,
  quotaRemaining
}) => {
  const navigate = useNavigate();
  // Track elapsed time during processing
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timeEstimate, setTimeEstimate] = useState(null);
  
  useEffect(() => {
    let interval;
    if (processing) {
      setElapsedTime(0);
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      setElapsedTime(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [processing]);
  
  // Update time estimate when file changes
  useEffect(() => {
    if (file) {
      setTimeEstimate(estimateGenerationTime(file.size));
    } else {
      setTimeEstimate(null);
    }
  }, [file]);
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.toLowerCase().endsWith('.stl')) {
      onFileSelect(selectedFile);
    }
  };

  return (
    <div className="bg-bg-light p-8 rounded-lg border border-text-muted/20 mb-6" data-testid="file-uploader">
      <div className="text-center">
        {!file ? (
          <label className={`block ${quotaRemaining === 0 ? '' : 'cursor-pointer'}`}>
            <input
              type="file"
              accept=".stl"
              onChange={handleFileChange}
              className="hidden"
              disabled={quotaRemaining === 0}
              data-testid="file-input"
            />
            <div className={`border-2 border-dashed rounded-lg p-12 transition-colors ${
              quotaRemaining === 0
                ? 'border-red-500/30 bg-red-500/[0.03]'
                : 'border-text-muted/30 hover:border-accent/50'
            }`}>
              <Layers className={`w-16 h-16 mx-auto mb-4 ${quotaRemaining === 0 ? 'text-red-400/70' : 'text-text-muted'}`} />
              <p className="text-white font-semibold mb-2">
                {quotaRemaining > 0 ? 'Click to upload STL file' : 'You\'ve reached your free quota'}
              </p>
              <p className="text-text-secondary text-sm">
                {quotaRemaining > 0
                  ? 'Maximum file size: 50MB'
                  : 'Subscribe to Pro for faster processing and higher quotas.'}
              </p>
              {quotaRemaining === 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    // Prevent the surrounding <label> from triggering the
                    // disabled file input — we only want the upgrade nav.
                    e.preventDefault();
                    e.stopPropagation();
                    trackCTAClick('Upgrade to Pro', 'uploader-quota-exhausted');
                    navigate('/dashboard/settings?tab=subscription');
                  }}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-bold shadow-md shadow-primary/25 hover:scale-[1.02] transition"
                  data-testid="uploader-upgrade-btn"
                >
                  <Zap className="w-4 h-4" />
                  Subscribe now — get faster processing
                </button>
              )}
            </div>
          </label>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3 text-white">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <span className="font-semibold" data-testid="selected-filename">{file.name}</span>
              <span className="text-text-secondary">
                ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </span>
            </div>
            
            {/* Time Estimate Banner */}
            {timeEstimate && !processing && !uploading && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-center justify-center gap-3">
                <Clock className="w-5 h-5 text-blue-400" />
                <span className="text-blue-400 text-sm">
                  <span className="font-medium">Estimated time:</span> {timeEstimate.label}
                </span>
                <Zap className="w-4 h-4 text-yellow-400" />
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-3">
              {/* Edit Support Points Button */}
              {onEditPoints && (
                <button
                  onClick={onEditPoints}
                  disabled={uploading || processing}
                  className="px-5 py-3 border-2 border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  data-testid="edit-points-btn"
                >
                  <Edit3 className="w-5 h-5" />
                  Edit Points
                </button>
              )}
              
              {/* Generate Supports Button */}
              <button
                onClick={onUpload}
                disabled={uploading || processing}
                className="px-6 py-3 bg-accent hover:bg-accent-hover text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="generate-supports-btn"
              >
                {uploading || processing ? (
                  <span className="flex items-center gap-2">
                    <Loader className="w-5 h-5 animate-spin" />
                    {uploading ? 'Uploading...' : 'Generating...'}
                  </span>
                ) : (
                  'Auto Generate'
                )}
              </button>
            </div>
            
            {/* Processing status with elapsed time - Only show if parent doesn't have progress indicator */}
            {processing && (
              <div className="bg-bg-dark/50 rounded-lg p-4 border border-accent/30">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <Loader className="w-5 h-5 text-accent animate-spin" />
                  <span className="text-white font-medium">Generating intelligent supports...</span>
                </div>
                <p className="text-text-secondary text-sm text-center">
                  Elapsed: {formatTime(elapsedTime)}
                  {timeEstimate && (
                    <span className="text-text-muted"> / Est. {timeEstimate.label}</span>
                  )}
                </p>
              </div>
            )}
            
            {/* Help text */}
            {!processing && !uploading && (
              <p className="text-text-secondary text-sm">
                <span className="text-purple-400">Edit Points</span>: Preview and customize support locations before generating
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploader;
